import {CareJourneyDetailsResponse, CareJourneyStep, TaskStepName} from '@/api/dto/care-journey';
import {Completion, StepName, StyledChip, Title} from '@/components/pages/tasks/task_edit/progress/TaskEditStep.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import TaskStepForm from "@/components/pages/tasks/task_edit/progress/TaskStepForm";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import Helpers from "@/helpers/helpers";
import {useContext, useMemo} from "react";
import Button from "patient-ping-remedy/packages/button";
import {printForm} from "@/helpers/form_helpers";
import {ClientProfile} from "@/api/dto/client-profile";
import {Model} from "survey-react-ui";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useMixpanel} from "@/hooks/useMixpanel";
import {MixpanelEventType, MixpanelPrintEvent} from "@/api/dto/mixpanel";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import {FilterInvalidate} from "@common/FilterComponent";
import ScheduleAppointmentActions
  from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentActions";
import Banner, {BannerTypes} from "patient-ping-remedy/packages/banner";
import {css} from "@emotion/css";
import {sizes} from "patient-ping-remedy/packages/theme";

type Props = {
  patient: ClientProfile;
  careJourneyStep: CareJourneyStep;
  careJourneyDetails: CareJourneyDetailsResponse;
  fetching: boolean;
  readOnly: boolean;
  invalidate: FilterInvalidate;
}

const getStepDescription = (stepName: string) => {
  switch (stepName) {
  case TaskStepName.SCHEDULED_APPOINTMENTS:
    return 'Schedule and view the status of appointment scheduling requests';
  default:
    return null;
  }
};

export default function TaskEditStep(
  { careJourneyStep, careJourneyDetails, fetching, patient, readOnly, invalidate }: Props) {
  const { taskStepDisplayValues } = useTaskStepName();
  const { mixpanelEventProperties } = useMixpanel();
  const { carecoApi } = useContext(CarecoApiContext);

  const showInitialOutreachIndicator = useMemo(() =>  careJourneyStep.stepName === TaskStepName.INITIAL_OUTREACH
    && careJourneyDetails.currentStep === TaskStepName.INITIAL_OUTREACH
    && careJourneyStep.attempts.length < 2
    && Helpers.calculateDaysHoursMinutesTimeDiff(
      new Date(careJourneyDetails.triggeringEncounterDateTime),
      new Date()
    ).totalHours > 48,
  [careJourneyStep, careJourneyDetails]);

  const printFormPdf =(careJourneyStep: CareJourneyStep) => {
    if (careJourneyStep.stepName === 'intake' && careJourneyStep.surveyJson) {
      const latestAttempt = careJourneyStep.attempts?.[careJourneyStep.attempts.length - 1];
      if (latestAttempt) {
        printForm(
          careJourneyStep.stepName ,
          new Model(careJourneyStep.surveyJson),
          latestAttempt.data,
          undefined ,
          patient.patient);
        if(mixpanelEventProperties) {
          const event: MixpanelPrintEvent = {
            eventName: MixpanelEventType.BIH_INTAKE_FORM_EXPORTED,
            formName: careJourneyStep.stepName,
            ...mixpanelEventProperties
          };
          carecoApi?.postMixpanelEvent(event);
        }
      }
    }
  };



  const titleDescription = getStepDescription(careJourneyStep.stepName);

  return (
    <div>
      <StepName>
        <Title>
          <div className={css({flexShrink: 0})}>
            <div>{taskStepDisplayValues[careJourneyStep.stepName as TaskStepName] ?? careJourneyStep.stepName}</div>
            <div>{titleDescription && <SmallTypography>{titleDescription}</SmallTypography>}</div>
          </div>
          <div className={css({
            display: 'flex',
            alignItems: 'center',
            gap: sizes.xSmall,
            marginLeft: 'auto',
            flexShrink: 0,
          })}>
            {careJourneyStep.stepName==='intake' &&
              <Button
                onClick={() => printFormPdf(careJourneyStep)}
                disabled={careJourneyStep.attempts.length === 0}
              >
                Print
              </Button>
            }
            {careJourneyStep.stepName === TaskStepName.SCHEDULED_APPOINTMENTS &&
             <ScheduleAppointmentActions
               fetching={fetching}
               careJourneyDetails={careJourneyDetails}
               careJourneyStep={careJourneyStep}
               patient={patient}
               invalidate={invalidate}
             />
            }
          </div>
        </Title>

        {careJourneyStep.assigneeType === 'AI' && (
          <Completion>
            {DisplayHelpers.humanize(careJourneyStep.status)} by Bamboo AI Agent
            <img src="/favicon.png" alt="AI Agent" height="16" />
          </Completion>
        )}
      </StepName>

      { showInitialOutreachIndicator &&
        <StyledChip>
          <Icon iconClass={ICONS.clock} /> Within 48 hours of event
        </StyledChip>
      }

      {careJourneyStep.statusReason &&
        <Banner type={BannerTypes.INFORMATIONAL} className={css({ marginTop: sizes.medium })}>
          {careJourneyStep.statusReason}
        </Banner>
      }

      <TaskStepForm
        readOnly={readOnly}
        fetching={fetching}
        careJourneyStep={careJourneyStep}
        careJourneyDetails={careJourneyDetails}
        patient={patient}
        invalidate={invalidate}
      />
    </div>
  );
}
